import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import JumboTextField from "@jumbo/components/JumboFormik/JumboTextField";
import { Field, useFormikContext } from "formik";
import { ColorField } from "app/pages/presentation/module/form/types";
import Div from "@jumbo/shared/Div";
import { Label } from "recharts";
import { RECOMMENDED_PRODUCTS_VIEW_TYPES } from "app/utils/constants/settings";

const FIELD_NAME = "SMARTKIOSK_CART";

const SmartkioskCart = ({
  handleChange,
  expanded,
  hideSectionTitle = false,
}) => {
  const { t, i18n } = useTranslation();
  const { values, setFieldValue } = useFormikContext();

  const content = (
    <>
      <Alert severity="info">
          <AlertTitle>Info</AlertTitle>
          {t("pages.dsokDetails.cart.maxItemsHelp")}
          <br />
          {t("pages.dsokDetails.cart.maxItemQuantityHelp")}
        </Alert>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4} lg={12}>
            <Typography sx={{ width: "33%", flexShrink: 0 }}>
              {t("pages.dsokDetails.cart.titleNotAuthUser")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                type="number"
                InputLabelProps={{ required: false }}
                fullWidth
                name="settings.smartkiosk.cartSettings.maxItems"
                label={t("pages.dsokDetails.cart.maxItems")}
                InputProps={{
                  value: values.settings?.smartkiosk?.cartSettings?.maxItems,
                  defaultValue: 0,
                  inputProps: {
                    min: 0,
                    max: 100,
                  },
                  onChange: (e) =>
                    setFieldValue(
                      "settings.smartkiosk.cartSettings.maxItems",
                      e.target.value
                    ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                type="number"
                InputLabelProps={{ required: false }}
                fullWidth
                name="settings.smartkiosk.cartSettings.maxItemQuantity"
                label={t("pages.dsokDetails.cart.maxItemQuantity")}
                InputProps={{
                  value:
                    values.settings?.smartkiosk?.cartSettings?.maxItemQuantity,
                  defaultValue: 0,
                  inputProps: {
                    min: 0,
                    max: 500,
                  },
                  onChange: (e) =>
                    setFieldValue(
                      "settings.smartkiosk.cartSettings.maxItemQuantity",
                      e.target.value
                    ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name="settings.smartkiosk.cartSettings.showBottomMessage"
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        "settings.smartkiosk.cartSettings.showBottomMessage",
                        e.target.checked
                      )
                    }
                  />
                }
                label={t("pages.dsokDetails.cart.showBottomMessage")}
                checked={
                  values.settings?.smartkiosk?.cartSettings?.showBottomMessage
                }
              />
            </Div>
          </Grid>
          <Grid item xs={12} md={4} lg={12}>
            <Typography sx={{ width: "33%", flexShrink: 0 }}>
              {t("pages.dsokDetails.cart.authUser.title")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} lg={12}>
            <Divider />
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                type="number"
                InputLabelProps={{ required: false }}
                fullWidth
                name="settings.smartkiosk.cartSettings.authUser.maxItems"
                label={t("pages.dsokDetails.cart.authUser.maxItems")}
                InputProps={{
                  value:
                    values.settings?.smartkiosk?.cartSettings?.authUser
                      ?.maxItems,
                  defaultValue: 0,
                  inputProps: {
                    min: 0,
                    max: 100,
                  },
                  onChange: (e) =>
                    setFieldValue(
                      "settings.smartkiosk.cartSettings.authUser.maxItems",
                      e.target.value
                    ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4} lg={2}>
            <FormControl sx={{ mt: 1, mb: 1 }} fullWidth>
              <JumboTextField
                type="number"
                InputLabelProps={{ required: false }}
                fullWidth
                name="settings.smartkiosk.cartSettings.authUser.maxItemQuantity"
                label={t("pages.dsokDetails.cart.authUser.maxItemQuantity")}
                InputProps={{
                  value:
                    values.settings?.smartkiosk?.cartSettings?.authUser
                      ?.maxItemQuantity,
                  defaultValue: 0,
                  inputProps: {
                    min: 0,
                    max: 500,
                  },
                  onChange: (e) =>
                    setFieldValue(
                      "settings.smartkiosk.cartSettings.authUser.maxItemQuantity",
                      e.target.value
                    ),
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2} lg={2}>
            <Div sx={{ mt: 2, mb: 1, mx: 0.5 }}>
              <FormControlLabel
                control={
                  <Field
                    name="settings.smartkiosk.cartSettings.authUser.showBottomMessage"
                    component={Switch}
                    onChange={(e) =>
                      setFieldValue(
                        "settings.smartkiosk.cartSettings.authUser.showBottomMessage",
                        e.target.checked
                      )
                    }
                  />
                }
                label={t("pages.dsokDetails.cart.showBottomMessage")}
                checked={
                  values.settings?.smartkiosk?.cartSettings?.authUser
                    ?.showBottomMessage
                }
              />
            </Div>
          </Grid>
        </Grid>
    </>
  );

  if (hideSectionTitle) {
    return content;
  }

  return (
    <Accordion
      expanded={expanded === FIELD_NAME}
      onChange={handleChange(FIELD_NAME)}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography sx={{ width: "33%", flexShrink: 0 }}>
          {t("pages.dsokDetails.cart.title")}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

export default SmartkioskCart;
